import { entities, enums, plainToInstance, selectors, utilities } from "@fraction/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { ChecklistApp } from "../api/types";
import { useFractionClient } from "./FractionClientContext";
import {
  APPLICATION_KEY,
  removeApplicationFromFetchCache,
  SINGLE_APPLICATION_KEY,
  updateApplicationInFetchCache,
} from "./useApplication";

export const resolveApprovalTargetStatus = (
  application: Pick<entities.ApplicationT, "status" | "brokers"> | null | undefined
): enums.ApplicationStatus =>
  application?.status === enums.ApplicationStatus.UNDERWRITING && application.brokers?.length
    ? enums.ApplicationStatus.FOR_BROKER_REVIEW
    : enums.ApplicationStatus.APPROVED_FOR_COMMITMENT_LETTER;

export interface ApplicationMutationsOptions {
  id?: string;
  enabled?: boolean;
  /** Returns the currently-cached application (for derived data like property id / last open status). */
  getApp?: () => ChecklistApp | undefined;
}

/**
 * Shared application mutations (core edits + deal lifecycle + employee actions)
 * for web and mobile. API calls use the shared FractionClient and optimistic
 * updates go through the shared `updateApplicationInFetchCache`, so web and
 * mobile keep their application caches in the same shape with one code path.
 *
 * Returns the raw mutation objects; each platform's wrapper exposes them in its
 * own shape (e.g. `updateApplication.mutateAsync`).
 */
export function useApplicationMutations({ id, enabled = true, getApp }: ApplicationMutationsOptions) {
  const client = useFractionClient();
  const queryClient = useQueryClient();
  const apply = (
    applicationId: string,
    updater: (prev: ChecklistApp | undefined | null) => ChecklistApp | null | undefined
  ) => updateApplicationInFetchCache(queryClient, applicationId, updater);

  const updateApplication = useMutation({
    mutationFn: async (
      changes_: Partial<entities.ApplicationT> & {
        cacheUpdateFn?: (
          prev: ChecklistApp | undefined | null,
          changes: Partial<entities.ApplicationT>
        ) => ChecklistApp | null | undefined;
      }
    ) => {
      const { cacheUpdateFn, ...changes } = changes_;
      if (!enabled || !id) {
        throw new Error("Not ready");
      }
      apply(
        id,
        cacheUpdateFn
          ? (prev) => cacheUpdateFn(prev, changes)
          : (prev) => ({ ...(prev || {}), ...changes, updatedAt: new Date() }) as ChecklistApp
      );
      return client.updateApplication({ id }, changes);
    },
    onSuccess: (deal) => {
      if (!id) {
        return;
      }
      apply(
        id,
        (prev) =>
          ({
            ...(prev || {}),
            ...utilities.object.deepClean(deal),
            updatedAt: new Date(),
          }) as ChecklistApp
      );
    },
  });

  const upsertApplicationSolicitor = useMutation({
    mutationFn: async (changes: Parameters<typeof client.upsertApplicationSolicitor>[1]) => {
      if (!enabled || !id) {
        throw new Error("Not ready");
      }
      apply(
        id,
        (prev) =>
          plainToInstance(entities.Application, {
            ...(prev || {}),
            solicitor: { ...(prev?.solicitor || {}), ...changes },
            updatedAt: new Date(),
          }) as ChecklistApp
      );
      const solicitor = await client.upsertApplicationSolicitor(id, changes);
      apply(
        id,
        (prev) =>
          ({
            ...(prev || {}),
            updatedAt: new Date(),
            solicitor: { ...(prev?.solicitor || {}), ...solicitor },
          }) as ChecklistApp
      );
    },
  });

  const updateApplicationProperty = useMutation({
    mutationFn: async (
      changes_: Parameters<typeof client.updateProperty>[2] & {
        /** Explicit property id; falls back to the cached app's property id. */
        propertyId?: string;
        cacheUpdateFn?: (
          prev: entities.Property | undefined | null,
          changes: Parameters<typeof client.updateProperty>[2]
        ) => entities.Property | null | undefined;
      }
    ) => {
      const { cacheUpdateFn, propertyId: explicitPropertyId, ...changes } = changes_;
      const propertyId = explicitPropertyId ?? getApp?.()?.property?.id;
      if (!enabled || !propertyId || !id) {
        throw new Error("Not ready");
      }
      apply(
        id,
        (prev) =>
          ({
            ...(prev || {}),
            updatedAt: new Date(),
            property: cacheUpdateFn
              ? cacheUpdateFn(prev?.property, changes)
              : { ...(prev?.property || {}), ...changes },
          }) as ChecklistApp
      );
      return client.updateProperty(id, propertyId, changes);
    },
    onSuccess: (property) => {
      if (!property?.applicationId) {
        return;
      }
      apply(
        property.applicationId,
        (prev) =>
          ({
            ...(prev || {}),
            updatedAt: new Date(),
            property: { ...(prev?.property || {}), ...property },
          }) as ChecklistApp
      );
    },
  });

  const updateApplicationLoan = useMutation({
    mutationFn: async (changes: Parameters<typeof client.updateLoan>[1]) => {
      if (!enabled || !id) {
        throw new Error("Not ready");
      }
      apply(
        id,
        (prev) =>
          plainToInstance(entities.Application, {
            ...(prev || {}),
            loan: { ...(prev?.loan || {}), ...changes },
            updatedAt: new Date(),
          }) as ChecklistApp
      );
      const loan = await client.updateLoan(id, changes);
      apply(
        id,
        (prev) =>
          ({
            ...(prev || {}),
            updatedAt: new Date(),
            loan: { ...(prev?.loan || {}), ...loan },
          }) as ChecklistApp
      );
    },
  });

  const rescindDeal = useMutation({
    mutationFn: async ({ withdrawnReason }: { withdrawnReason: string }) => {
      if (!id) {
        throw new Error("No id");
      }
      apply(
        id,
        (prev) => ({ ...(prev || {}), status: enums.ApplicationStatus.HOMEOWNER_RESCINDED }) as ChecklistApp
      );
      return client.rescindDeal(id, withdrawnReason);
    },
  });

  /**
   * Soft-delete the deal. The backend also rescinds it, archives its Slack channel and
   * moves the Hubspot deal to Closed Lost, so the row is gone from every application
   * list afterwards — callers should navigate away and drop the cached entry rather
   * than optimistically patching it.
   */
  const deleteDeal = useMutation({
    mutationFn: async ({ withdrawnReason }: { withdrawnReason?: string } = {}) => {
      if (!id) {
        throw new Error("No id");
      }
      await client.closeApplication(id, withdrawnReason);
    },
    onSuccess: () => {
      if (id) {
        removeApplicationFromFetchCache(queryClient, id);
      }
      queryClient.invalidateQueries({ queryKey: APPLICATION_KEY });
    },
  });

  const rejectDeal = useMutation({
    mutationFn: async ({ rejectReasons }: { rejectReasons: enums.DeclineReason[] }) => {
      if (!id) {
        throw new Error("No id");
      }
      apply(
        id,
        (prev) => ({ ...(prev || {}), status: enums.ApplicationStatus.PENDING_DECLINE }) as ChecklistApp
      );
      return client.rejectDeal(id, rejectReasons);
    },
  });

  const approveDeal = useMutation({
    mutationFn: async ({
      underwritingNotes,
      targetStatus,
    }: {
      underwritingNotes: string;
      targetStatus?: enums.ApplicationStatus;
    }) => {
      if (!id) {
        throw new Error("No id");
      }
      const application =
        getApp?.() ?? queryClient.getQueryData<ChecklistApp>([...SINGLE_APPLICATION_KEY, id]);
      const status = targetStatus ?? resolveApprovalTargetStatus(application);
      apply(
        id,
        (prev) =>
          ({
            ...(prev || {}),
            status,
          }) as ChecklistApp
      );
      return client.approveDeal(id, underwritingNotes, status);
    },
  });

  const reopenDeal = useMutation({
    mutationFn: async () => {
      const app = getApp?.();
      if (!id || !app) {
        throw new Error("No id or app");
      }
      const lastOpenStatus = selectors.application.selectMostRecentOpenStatus(app);
      apply(id, (prev) => ({ ...(prev || {}), status: lastOpenStatus?.status }) as ChecklistApp);
      return client.reopenDeal(id);
    },
    onSuccess: (deal) => {
      if (deal?.id) {
        apply(deal.id, (prev) => ({ ...(prev || {}), ...deal }) as ChecklistApp);
      }
    },
  });

  const setProductInterest = useMutation({
    mutationFn: async ({ evaluationId, requested }: { evaluationId: string; requested: boolean }) => {
      if (!id) {
        throw new Error("No id");
      }
      const now = requested ? new Date() : null;
      apply(id, (prev) =>
        prev
          ? ({
              ...prev,
              productRateEvaluations: prev.productRateEvaluations?.map((e) =>
                e.id === evaluationId ? { ...e, borrowerRequestedAt: now } : e
              ),
            } as ChecklistApp)
          : prev
      );
      const result = await client.setEvaluationBorrowerRequested(id, evaluationId, requested);
      apply(id, (prev) =>
        prev
          ? ({
              ...prev,
              status: result.applicationStatus,
              ...(result.applicationAdvanced ? { status_change_for_submit: new Date() } : {}),
            } as ChecklistApp)
          : prev
      );
      return result;
    },
  });

  // Split a flat `path -> value` change set (e.g. "property.city", "loan.rate",
  // "solicitor.name", "requestedAmount") and apply each to the right entity.
  const saveFields = useCallback(
    async (
      changes: Record<string, any>,
      opts?: {
        cacheUpdateFnProperty?: (
          prev: entities.Property | undefined | null,
          changes: entities.Property
        ) => entities.Property | null | undefined;
      }
    ) => {
      const propertyChanges: Record<string, any> = {};
      const solicitorChanges: Record<string, any> = {};
      const loanChanges: Record<string, any> = {};
      const applicationChanges: Record<string, any> = {};

      for (const [path, value] of Object.entries(changes)) {
        if (path.startsWith("property.")) {
          propertyChanges[path.slice(9)] = value;
        } else if (path.startsWith("solicitor.")) {
          solicitorChanges[path.slice(10)] = value;
        } else if (path.startsWith("loan.")) {
          loanChanges[path.slice(5)] = value;
        } else {
          applicationChanges[path] = value;
        }
      }

      const promises: Promise<unknown>[] = [];
      if (Object.keys(propertyChanges).length) {
        promises.push(
          updateApplicationProperty.mutateAsync({
            ...propertyChanges,
            propertyId: getApp?.()?.property?.id,
            ...(opts?.cacheUpdateFnProperty && { cacheUpdateFn: opts.cacheUpdateFnProperty }),
          })
        );
      }
      if (Object.keys(solicitorChanges).length) {
        promises.push(upsertApplicationSolicitor.mutateAsync(solicitorChanges));
      }
      if (Object.keys(loanChanges).length) {
        promises.push(updateApplicationLoan.mutateAsync(loanChanges));
      }
      if (Object.keys(applicationChanges).length) {
        promises.push(updateApplication.mutateAsync(applicationChanges));
      }
      await Promise.all(promises);
    },
    [getApp, updateApplication, updateApplicationProperty, updateApplicationLoan, upsertApplicationSolicitor]
  );

  const isPending =
    updateApplication.isPending ||
    updateApplicationProperty.isPending ||
    upsertApplicationSolicitor.isPending ||
    updateApplicationLoan.isPending;

  return {
    updateApplication,
    updateApplicationProperty,
    updateApplicationLoan,
    upsertApplicationSolicitor,
    rescindDeal,
    deleteDeal,
    rejectDeal,
    approveDeal,
    reopenDeal,
    setProductInterest,
    saveFields,
    isPending,
  };
}
